(ns minorodata.dependency-graph.timeline.window
  "Date arithmetic behind the timeline's period controls and its start/finish
   cells. Kept out of the view so it can be exercised without one."
  (:require
   ["date-fns" :refer [addHours endOfHour format startOfHour]]
   ["date-fns-tz" :refer [formatInTimeZone]]
   [ui.status-vocab :refer [normalize-status]]))


(defn valid-date?
  "Whether `d` is a Date a formatter will accept — date-fns throws on the rest."
  [d]
  (and (instance? js/Date d) (not (js/isNaN (.getTime d)))))


(defn safe-format
  "`format`, or nil when the date is unusable."
  [d pattern]
  (when (valid-date? d)
    (format d pattern)))


(defn hours-ending-now
  "`n` whole hours, ending at the end of the current hour, so the bounds land on
   the hour grid they are drawn against. \"Last hour\" therefore means the hour
   you are in, not the preceding 60 minutes."
  [now n]
  {:started-at (addHours (startOfHour now) (- (dec n)))
   :ended-at   (endOfHour now)})


(defn- end-ms
  "When an execution finished, or `now` while it is still going."
  [{:keys [finished-at]} now]
  (.getTime (if (valid-date? finished-at) finished-at now)))

(defn- run-span
  "`[start-ms end-ms]` over a run's started executions, or nil."
  [started now]
  (when (seq started)
    (let [start (apply min (map #(.getTime (:started-at %)) started))]
      ;; floored at `start`: a browser clock behind the server's would invert it
      [start (apply max start (map #(end-ms % now) started))])))

(defn- started [executions]
  (filter #(valid-date? (:started-at %)) executions))

(defn run-window
  "Window framing a run: first start to last finish (`now` if still going), plus a
   margin. Nil when nothing started."
  [executions now]
  (when-let [[start end] (run-span (started executions) now)]
    (let [margin (max 5000 (* 0.05 (- end start)))]
      {:started-at (js/Date. (- start margin))
       :ended-at   (js/Date. (+ end margin))})))


(defn same-day?
  "Whether two instants land on the same date in `timezone` — the timezone the
   cells are rendered in, so this answers what the reader actually sees."
  [timezone a b]
  (boolean
   ;; date-fns-tz throws on an invalid date, as `format` does.
   (and (valid-date? a) (valid-date? b)
        (= (formatInTimeZone a timezone "yyyy-MM-dd")
           (formatInTimeZone b timezone "yyyy-MM-dd")))))


(defn window-text
  "The window in words, or nil if either end is unusable."
  [timezone {:keys [started-at ended-at]}]
  (when (and (valid-date? started-at) (valid-date? ended-at))
    (str (format started-at "d MMM HH:mm")
         " – "
         (format ended-at (if (same-day? timezone started-at ended-at)
                            "HH:mm"
                            "d MMM HH:mm")))))


(defn run-outcome
  "A run's status from its executions': running, else failed, else succeeded if all did."
  [statuses]
  (let [normal (set (map normalize-status statuses))]
    (cond
      (some normal [:running :queued]) :running
      (normal :failed)                 :failed
      (= #{:succeeded} normal)         :succeeded
      :else                            :other)))


(defn run-summary
  "Counts, end-to-end span and slowest execution of a run. Nil when nothing started."
  [executions now]
  (let [ran  (started executions)
        took #(- (end-ms % now) (.getTime (:started-at %)))]
    (when-let [[start end] (run-span ran now)]
      (let [normal  (map (comp normalize-status :status) executions)
            slowest (apply max-key took ran)]
        {:transforms (count (into #{} (map :master-id) executions))
         :failed     (count (filter #{:failed} normal))
         :running    (count (filter #{:running :queued} normal))
         :span-ms    (- end start)
         :slowest    {:master-id (:master-id slowest) :ms (took slowest)}}))))
